import React, { useEffect, useMemo, useState } from 'react';
import {
  useQuizzesByCourses,
  useAttemptReview,
  useAttemptSummary,
  useQuizUserAttempts,
} from '../../../../hooks/useMoodleQuiz';
import QuizResultPage from './QuizResultPage';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import type { MoodleInstance } from '../../../../hooks/useMoodleHooks';

interface Props {
  courseId: string | number;
  quizId: string | number;
  attemptId: string | number;
  instance: MoodleInstance;
  onContinue?: () => void;
  onBackToEntry?: () => void;
}

const SafeLoader: React.FC<{ isLoading: boolean; message?: string; children?: React.ReactNode }> = ({
                                                                                                      isLoading,
                                                                                                      message,
                                                                                                      children,
                                                                                                    }) => {
  if (!isLoading) return <>{children}</>;
  try {
    return <StbLoader isLoading={true} />;
  } catch {
    return <div>{message || 'Loading...'}</div>;
  }
};

export default function MoodleQuizResult({
                                           courseId,
                                           quizId,
                                           attemptId,
                                           instance,
                                           onContinue,
                                           onBackToEntry,
                                         }: Props) {
  const cid = Number(courseId);
  const qid = Number(quizId);
  const aid = Number(attemptId);

  const {
    data: quizzesInCourse,
    isLoading: loadingQuizzes,
    error: quizzesError,
  } = useQuizzesByCourses(cid, { enabled: !!cid }, instance);

  const {
    data: summary,
    isLoading,
    refetch: refetchSummary,
    error: summaryError,
  } = useAttemptSummary(aid, undefined, { enabled: !!aid }, instance);

  const {
    data: review,
    isLoading: loadingReview,
    error: reviewError,
  } = useAttemptReview(
    aid,
    undefined,
    {
      enabled:
        !!aid &&
        String((summary as any)?.errorcode || '').toLowerCase() === 'attemptalreadyclosed',
    },
    instance
  );

  const {
    data: userAttempts,
    isLoading: loadingAttempts,
    error: attemptsError,
  } = useQuizUserAttempts(qid, undefined, { enabled: !!qid }, instance);

  const [waiting, setWaiting] = useState(false);

  const normalizedAttempts = useMemo(() => {
    if (Array.isArray(userAttempts)) return userAttempts;
    if (Array.isArray((userAttempts as any)?.attempts)) return (userAttempts as any).attempts;
    if (Array.isArray((userAttempts as any)?.usersattempts)) return (userAttempts as any).usersattempts;
    if (Array.isArray((userAttempts as any)?.quizzes)) return (userAttempts as any).quizzes;
    return [];
  }, [userAttempts]);

  const isDone = useMemo(() => {
    const summaryDone =
      summary &&
      (String((summary as any).state || '').toLowerCase() === 'finished' ||
        String((summary as any).errorcode || '').toLowerCase() === 'attemptalreadyclosed');

    const reviewDone =
      review &&
      String((review as any)?.attempt?.state || '').toLowerCase() === 'finished';

    return !!summaryDone || !!reviewDone;
  }, [summary, review]);

  useEffect(() => {
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    if (!isDone && summary) {
      setWaiting(true);
      let tries = 0;

      const poll = () => {
        if (cancelled) return;

        refetchSummary().then((res) => {
          if (cancelled) return;

          const state = String((res.data as any)?.state || '').toLowerCase();
          const errorCode = String((res.data as any)?.errorcode || '').toLowerCase();

          if (state === 'finished' || errorCode === 'attemptalreadyclosed') {
            setWaiting(false);
          } else if (tries < 15) {
            tries++;
            timeout = setTimeout(poll, 700);
          } else {
            setWaiting(false);
          }
        });
      };

      poll();
    }

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      setWaiting(false);
    };
  }, [summary, refetchSummary, isDone]);

  const { isPassed, scorePercent } = useMemo(() => {
    const quizData = quizzesInCourse?.find((q: any) => q.id === qid);
    if (!quizData) return { isPassed: false, scorePercent: null };

    const rawMax = Number((quizData as any).sumgrades ?? NaN);
    const gradebookMax = Number((quizData as any).grade ?? NaN);
    let gradeToPass = Number((quizData as any).gradetopass ?? NaN);

    if (
      !Number.isFinite(gradeToPass) &&
      typeof (quizData as any).completionpass === 'number' &&
      Number.isFinite(gradebookMax)
    ) {
      gradeToPass = gradebookMax * (quizData as any).completionpass;
    }

    const computeResult = (attemptScoreValue: any) => {
      const attemptScore = Number(attemptScoreValue ?? NaN);
      const scorePercent = rawMax > 0 && Number.isFinite(attemptScore) ? (attemptScore / rawMax) * 100 : null;

      const requiredPercent =
        gradebookMax > 0 && Number.isFinite(gradeToPass) ? gradeToPass / gradebookMax : NaN;
      const achievedPercent = scorePercent !== null ? scorePercent / 100 : NaN;
      const passed =
        Number.isFinite(requiredPercent) &&
        Number.isFinite(achievedPercent) &&
        achievedPercent >= requiredPercent;

      return { isPassed: passed, scorePercent };
    };

    if (summary && !(summary as any).errorcode) {
      return computeResult((summary as any).sumgrades);
    }

    if (String((summary as any)?.errorcode || '').toLowerCase() === 'attemptalreadyclosed') {
      const thisAttempt =
        normalizedAttempts.find((a: any) => Number(a.id) === aid) ||
        normalizedAttempts
          .filter((a: any) => String(a.state).toLowerCase() === 'finished')
          .sort((a: any, b: any) => Number(b.timefinish || 0) - Number(a.timefinish || 0))[0];

      if (thisAttempt) {
        return computeResult((thisAttempt as any).sumgrades ?? (thisAttempt as any).grade);
      }

      if ((review as any)?.attempt) {
        return computeResult((review as any).attempt.sumgrades ?? (review as any).grade);
      }
    }

    if ((review as any)?.attempt) {
      return computeResult((review as any).attempt.sumgrades ?? (review as any).grade);
    }

    return { isPassed: false, scorePercent: null };
  }, [summary, review, quizzesInCourse, normalizedAttempts, qid, aid]);

  useEffect(() => {
    if (isPassed) {
      window.dispatchEvent(new CustomEvent('moodle:activityCompleted'));
    }
  }, [isPassed]);

  if (!courseId || !quizId || !attemptId || isNaN(cid) || isNaN(qid) || isNaN(aid)) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Invalid Quiz Result</h3>
        <p>Unable to load quiz results due to invalid parameters.</p>
        {onBackToEntry && (
          <button className="btn btn-outline" onClick={onBackToEntry}>
            Back to Quiz
          </button>
        )}
      </div>
    );
  }

  if (quizzesError) {
    return <p role="alert" style={{ color: 'red' }}>{(quizzesError as Error).message}</p>;
  }

  if (summaryError) {
    return <p role="alert" style={{ color: 'red' }}>{(summaryError as Error).message}</p>;
  }

  if (attemptsError) {
    return <p role="alert" style={{ color: 'red' }}>{(attemptsError as Error).message}</p>;
  }

  if (reviewError && String((summary as any)?.errorcode || '').toLowerCase() === 'attemptalreadyclosed') {
    return <p role="alert" style={{ color: 'red' }}>{(reviewError as Error).message}</p>;
  }

  if (isLoading || loadingQuizzes || loadingAttempts || loadingReview || !isDone || waiting) {
    return <SafeLoader isLoading={true} message="Loading quiz results..." />;
  }

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {onBackToEntry && (
        <div>
          <button className="btn btn-outline" onClick={onBackToEntry}>
            ← Back to Quiz
          </button>
        </div>
      )}

      <QuizResultPage
        isPassed={isPassed}
        scorePercent={scorePercent}
        onContinue={() => onContinue?.()}
      />
    </div>
  );
}